import { cn } from '@/lib/utils';
import { Component, ErrorInfo, ReactNode } from 'react';
// Removed design-system import - it causes platform graph errors when used in ErrorBoundary
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { trackError, isPlatformErrorId } from '@/utils/errorTracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

interface ErrorFallbackProps {
  error: Error | null;
  errorId: string | null;
  onRetry: () => void;
  onReload: () => void;
}

/**
 * ErrorFallback renders outside all providers, so it CANNOT use hooks like
 * useLocalizations. Hardcoded strings are intentional here.
 * 
 * NOTE: This component intentionally avoids @/design-system imports to prevent
 * circular dependencies during platform graph resolution.
 */
function ErrorFallback({ error, errorId, onRetry, onReload }: ErrorFallbackProps) {
  return (
    <div className="p-6 min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4 mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>Something went wrong</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-center text-muted-foreground">
            An error occurred. Please try again.
          </p>
          {errorId && (
            <div className="text-center space-y-1">
              <p className="text-xs text-muted-foreground font-mono">
                Error ID: {errorId}
              </p>
              {isPlatformErrorId(errorId) && (
                <p className="text-xs text-muted-foreground">
                  This is a platform error. Please contact Lovable support with this ID and timestamp.
                </p>
              )}
            </div>
          )}
          {error && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Technical Details
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-32">
                {error.message}
              </pre>
            </details>
          )}
        </CardContent>
        <CardFooter className="flex gap-2 justify-center">
          <Button variant="outline" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
          <Button onClick={onReload}>
            Reload Page
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorId: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = trackError(error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      metadata: { componentStack: errorInfo.componentStack },
    });
    this.setState({ errorId });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorId: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorId={this.state.errorId}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}
